import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUpRight, ArrowDownRight, Minus, GitCompare, HelpCircle } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { format, subMonths, subYears, startOfMonth, endOfMonth, startOfYear, endOfYear, startOfQuarter, endOfQuarter, subQuarters } from 'date-fns';

interface PeriodData {
  totalSavings: number;
  homeSavings: number;
  pcpSavings: number;
  urgentCareSavings: number;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
  totalCases: number;
}

interface CostDefaults {
  HOME: number;
  PCP: number;
  URGENT_CARE: number;
  ER_NOW: number;
}

type RecommendationType = 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW';

interface CostSavingsComparisonProps {
  agencyId: string | null;
  customHospitalCost?: number | null;
  selectedRecommendations?: RecommendationType[];
  patientIds?: string[];
}

type PeriodType = 'this_month_vs_last' | 'this_quarter_vs_last' | 'this_year_vs_last' | 'last_3_months_vs_prior' | 'last_6_months_vs_prior';

const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: 'this_month_vs_last', label: 'This Month vs Last Month' },
  { value: 'this_quarter_vs_last', label: 'This Quarter vs Last Quarter' },
  { value: 'this_year_vs_last', label: 'This Year vs Last Year' },
  { value: 'last_3_months_vs_prior', label: 'Last 3 Months vs Prior 3 Months' },
  { value: 'last_6_months_vs_prior', label: 'Last 6 Months vs Prior 6 Months' },
];

export default function CostSavingsComparison({ 
  agencyId, 
  customHospitalCost, 
  selectedRecommendations = ['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'],
  patientIds
}: CostSavingsComparisonProps) {
  const chartColors = useChartColors();
  const [periodType, setPeriodType] = useState<PeriodType>('this_month_vs_last');
  const [currentPeriod, setCurrentPeriod] = useState<PeriodData | null>(null);
  const [previousPeriod, setPreviousPeriod] = useState<PeriodData | null>(null);
  const [costDefaults, setCostDefaults] = useState<CostDefaults>({
    HOME: 150,
    PCP: 450,
    URGENT_CARE: 500,
    ER_NOW: 2500
  });
  const [loading, setLoading] = useState(true);
  const [periodLabels, setPeriodLabels] = useState({ current: '', previous: '' });

  useEffect(() => {
    loadCostDefaults();
  }, []);

  useEffect(() => {
    loadComparisonData();
  }, [agencyId, periodType, costDefaults, customHospitalCost, selectedRecommendations]);

  const loadCostDefaults = async () => {
    try {
      const { data, error } = await supabase
        .from('hospital_visit_cost_defaults')
        .select('recommendation_level, default_cost');

      if (error) throw error;

      if (data) {
        const defaults: CostDefaults = { HOME: 150, PCP: 450, URGENT_CARE: 500, ER_NOW: 2500 };
        data.forEach((row: { recommendation_level: string; default_cost: number }) => {
          if (row.recommendation_level in defaults) {
            defaults[row.recommendation_level as keyof CostDefaults] = row.default_cost;
          }
        });
        setCostDefaults(defaults);
      }
    } catch (error) {
      console.error('Error loading cost defaults:', error);
    }
  };

  const getDateRanges = (type: PeriodType) => {
    const now = new Date();
    
    switch (type) {
      case 'this_month_vs_last':
        return {
          current: { start: startOfMonth(now), end: endOfMonth(now), label: format(now, 'MMMM yyyy') },
          previous: { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)), label: format(subMonths(now, 1), 'MMMM yyyy') }
        };
      case 'this_quarter_vs_last':
        return {
          current: { start: startOfQuarter(now), end: endOfQuarter(now), label: `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}` },
          previous: { start: startOfQuarter(subQuarters(now, 1)), end: endOfQuarter(subQuarters(now, 1)), label: `Q${Math.ceil((subQuarters(now, 1).getMonth() + 1) / 3)} ${subQuarters(now, 1).getFullYear()}` }
        };
      case 'this_year_vs_last':
        return {
          current: { start: startOfYear(now), end: endOfYear(now), label: now.getFullYear().toString() },
          previous: { start: startOfYear(subYears(now, 1)), end: endOfYear(subYears(now, 1)), label: subYears(now, 1).getFullYear().toString() }
        };
      case 'last_3_months_vs_prior':
        return {
          current: { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now), label: `${format(subMonths(now, 2), 'MMM')} - ${format(now, 'MMM yyyy')}` },
          previous: { start: startOfMonth(subMonths(now, 5)), end: endOfMonth(subMonths(now, 3)), label: `${format(subMonths(now, 5), 'MMM')} - ${format(subMonths(now, 3), 'MMM yyyy')}` }
        };
      case 'last_6_months_vs_prior':
        return {
          current: { start: startOfMonth(subMonths(now, 5)), end: endOfMonth(now), label: `${format(subMonths(now, 5), 'MMM')} - ${format(now, 'MMM yyyy')}` },
          previous: { start: startOfMonth(subMonths(now, 11)), end: endOfMonth(subMonths(now, 6)), label: `${format(subMonths(now, 11), 'MMM')} - ${format(subMonths(now, 6), 'MMM yyyy')}` }
        };
    }
  };

  const calculatePeriodData = (sessions: any[], effectiveErCost: number): PeriodData => {
    // Filter sessions by selected recommendations
    const filteredSessions = sessions.filter(s => 
      selectedRecommendations.includes(s.recommendation as RecommendationType)
    );

    const data: PeriodData = {
      totalSavings: 0,
      homeSavings: 0,
      pcpSavings: 0,
      urgentCareSavings: 0,
      homeCases: 0,
      pcpCases: 0,
      urgentCareCases: 0,
      erCases: 0,
      totalCases: filteredSessions.length
    };

    filteredSessions.forEach(session => {
      switch (session.recommendation) {
        case 'HOME':
          data.homeCases++;
          data.homeSavings += effectiveErCost - costDefaults.HOME;
          break;
        case 'PCP':
          data.pcpCases++;
          data.pcpSavings += effectiveErCost - costDefaults.PCP;
          break;
        case 'URGENT_CARE':
          data.urgentCareCases++;
          data.urgentCareSavings += effectiveErCost - costDefaults.URGENT_CARE;
          break;
        case 'ER_NOW':
          data.erCases++;
          break;
      }
    });

    data.totalSavings = data.homeSavings + data.pcpSavings + data.urgentCareSavings;
    return data;
  };

  const loadComparisonData = async () => {
    setLoading(true);
    try {
      const ranges = getDateRanges(periodType);
      setPeriodLabels({ current: ranges.current.label, previous: ranges.previous.label });

      const effectiveErCost = customHospitalCost || costDefaults.ER_NOW;

      // Fetch current period data
      let currentQuery = supabase
        .from('triage_sessions')
        .select('recommendation, completed_at')
        .not('completed_at', 'is', null)
        .gte('completed_at', ranges.current.start.toISOString())
        .lte('completed_at', ranges.current.end.toISOString());
      
      if (agencyId) {
        currentQuery = currentQuery.eq('agency_id', agencyId);
      }
      
      const { data: currentSessions, error: currentError } = await currentQuery;

      if (currentError) throw currentError;

      // Fetch previous period data
      let previousQuery = supabase
        .from('triage_sessions')
        .select('recommendation, completed_at')
        .not('completed_at', 'is', null)
        .gte('completed_at', ranges.previous.start.toISOString())
        .lte('completed_at', ranges.previous.end.toISOString());
      
      if (agencyId) {
        previousQuery = previousQuery.eq('agency_id', agencyId);
      }
      
      const { data: previousSessions, error: previousError } = await previousQuery;

      if (previousError) throw previousError;

      setCurrentPeriod(calculatePeriodData(currentSessions || [], effectiveErCost));
      setPreviousPeriod(calculatePeriodData(previousSessions || [], effectiveErCost));
    } catch (error) {
      console.error('Error loading comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getPercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const ChangeIndicator = ({ current, previous, isPositiveGood = true }: { current: number; previous: number; isPositiveGood?: boolean }) => {
    const change = getPercentageChange(current, previous);
    const isPositive = change > 0;
    const isNeutral = change === 0;
    
    const colorClass = isNeutral 
      ? 'text-muted-foreground' 
      : (isPositive === isPositiveGood) 
        ? 'text-green-600' 
        : 'text-red-600';

    const bgClass = isNeutral 
      ? 'bg-muted' 
      : (isPositive === isPositiveGood) 
        ? 'bg-green-100' 
        : 'bg-red-100';

    return (
      <Badge variant="secondary" className={`${bgClass} ${colorClass} gap-1`}>
        {isNeutral ? (
          <Minus className="h-3 w-3" />
        ) : isPositive ? (
          <ArrowUpRight className="h-3 w-3" />
        ) : (
          <ArrowDownRight className="h-3 w-3" />
        )}
        {Math.abs(change).toFixed(1)}%
      </Badge>
    );
  };

  const chartData = currentPeriod && previousPeriod ? [
    { name: 'Home Care', current: currentPeriod.homeSavings, previous: previousPeriod.homeSavings },
    { name: 'PCP', current: currentPeriod.pcpSavings, previous: previousPeriod.pcpSavings },
    { name: 'Urgent Care', current: currentPeriod.urgentCareSavings, previous: previousPeriod.urgentCareSavings },
  ] : [];

  const casesChartData = currentPeriod && previousPeriod ? [
    { name: 'Home Care', current: currentPeriod.homeCases, previous: previousPeriod.homeCases },
    { name: 'PCP', current: currentPeriod.pcpCases, previous: previousPeriod.pcpCases },
    { name: 'Urgent Care', current: currentPeriod.urgentCareCases, previous: previousPeriod.urgentCareCases },
    { name: 'ER Required', current: currentPeriod.erCases, previous: previousPeriod.erCases },
  ] : [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="rounded-lg shadow-lg p-3 border bg-white"
          style={chartColors.tooltipStyle}
        >
          <p className="font-medium mb-2 text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">
                {entry.dataKey.includes('Savings') || entry.name.includes('Savings') 
                  ? formatCurrency(entry.value) 
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <GitCompare className="h-5 w-5" />
            <span>Period Comparison</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse h-64 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <GitCompare className="h-5 w-5 text-purple-600" />
              <span>Period Comparison</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">Has its own period selector. Not affected by the date filter above.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              Compare cost savings between different time periods
            </CardDescription>
          </div>
          <Select value={periodType} onValueChange={(v) => setPeriodType(v as PeriodType)}>
            <SelectTrigger className="w-[260px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Savings</span>
              {currentPeriod && previousPeriod && (
                <ChangeIndicator current={currentPeriod.totalSavings} previous={previousPeriod.totalSavings} />
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.current}</span>
                <span className="text-lg font-bold text-green-600">{formatCurrency(currentPeriod?.totalSavings || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.previous}</span>
                <span className="text-sm text-muted-foreground">{formatCurrency(previousPeriod?.totalSavings || 0)}</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Cases</span>
              {currentPeriod && previousPeriod && (
                <ChangeIndicator current={currentPeriod.totalCases} previous={previousPeriod.totalCases} />
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.current}</span>
                <span className="text-lg font-bold text-foreground">{currentPeriod?.totalCases || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.previous}</span>
                <span className="text-sm text-muted-foreground">{previousPeriod?.totalCases || 0}</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Cases Diverted from ER</span>
              {currentPeriod && previousPeriod && (
                <ChangeIndicator 
                  current={currentPeriod.homeCases + currentPeriod.pcpCases + currentPeriod.urgentCareCases} 
                  previous={previousPeriod.homeCases + previousPeriod.pcpCases + previousPeriod.urgentCareCases} 
                />
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.current}</span>
                <span className="text-lg font-bold text-blue-600">
                  {(currentPeriod?.homeCases || 0) + (currentPeriod?.pcpCases || 0) + (currentPeriod?.urgentCareCases || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{periodLabels.previous}</span>
                <span className="text-sm text-muted-foreground">
                  {(previousPeriod?.homeCases || 0) + (previousPeriod?.pcpCases || 0) + (previousPeriod?.urgentCareCases || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Savings by Category</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: chartColors.axis }} tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 12, fill: chartColors.axis }} tickLine={false} axisLine={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="current" name={periodLabels.current} fill="#10B981" radius={[4, 4, 0, 0]} animationDuration={1200} animationEasing="ease-out" />
                  <Bar dataKey="previous" name={periodLabels.previous} fill="#8B5CF6" radius={[4, 4, 0, 0]} animationDuration={1200} animationEasing="ease-out" animationBegin={200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Cases by Type</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={casesChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: chartColors.axis }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: chartColors.axis }} tickLine={false} axisLine={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="current" name={periodLabels.current} fill="#10B981" radius={[4, 4, 0, 0]} animationDuration={1200} animationEasing="ease-out" />
                  <Bar dataKey="previous" name={periodLabels.previous} fill="#8B5CF6" radius={[4, 4, 0, 0]} animationDuration={1200} animationEasing="ease-out" animationBegin={200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
